import React, { useState } from 'react';
import MainLayout from '@/components/layouts/MainLayout';
import PageHeader from '@/components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Eye, Trash2 } from 'lucide-react';
import { useAuthors } from '@/hooks/useAuthors';
import { useDeleteAuthor } from '@/hooks/useDeleteAuthor';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import AddAuthorDialog from '@/components/admin/AddAuthorDialog';
import EditAuthorDialog from '@/components/admin/EditAuthorDialog';
import FixEmailDialog from '@/components/admin/FixEmailDialog';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const AdminAuthors: React.FC = () => {
  const { data: authors, isLoading } = useAuthors();
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { toast } = useToast();
  const deleteAuthor = useDeleteAuthor();

  // Filter authors based on search query
  const filteredAuthors = React.useMemo(() => {
    if (!authors) return [];
    
    if (!searchQuery) return authors;
    
    const query = searchQuery.toLowerCase();
    
    return authors.filter(author => 
      author.name.toLowerCase().includes(query) || 
      author.email.toLowerCase().includes(query)
    );
  }, [authors, searchQuery]);
  
  // Function to get avatar initials
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  // Function to handle navigation to author details
  const handleViewAuthor = (authorId: string) => {
    navigate(`/admin/authors/${authorId}`);
  };

  // Function to handle navigation to author's books
  const handleViewBooks = (authorId: string) => {
    navigate(`/admin/authors/${authorId}/books`);
  };

  // Function to handle navigation to author's royalties
  const handleViewRoyalties = (authorId: string) => {
    navigate(`/admin/authors/${authorId}/royalties`);
  };

  const handleDeleteAuthor = async (authorId: string, authorName: string) => {
    try {
      await deleteAuthor.mutateAsync(authorId);
      toast({
        title: 'Success',
        description: `${authorName} has been deleted successfully.`,
      });
    } catch (error) {
      console.error('Error deleting author:', error);
    }
  };

  return (
    <MainLayout allowedRoles={['admin']}>
      <PageHeader
        title="Authors"
        description="Manage all authors and their royalties"
      />
      
      <div className="mb-6">
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <CardTitle>Author Directory</CardTitle>
              
              <div className="flex w-full md:w-auto items-center gap-2">
                <div className="relative w-full md:w-80">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search authors..."
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                
                <AddAuthorDialog />
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center p-8">
                <p>Loading authors...</p>
              </div>
            ) : filteredAuthors.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[250px]">Author</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-center">Role</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAuthors.map(author => (
                    <TableRow key={author.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={author.avatar_url} />
                            <AvatarFallback className="bg-royalty-600 text-white">
                              {getInitials(author.name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{author.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>{author.email}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="capitalize">
                          {author.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewAuthor(author.id)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          <EditAuthorDialog author={author} />
                          <FixEmailDialog author={author} />
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                disabled={deleteAuthor.isPending}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This action cannot be undone. This will permanently delete {author.name}
                                  and remove all their data including books, royalty reports, payment requests, support tickets, and their user account.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDeleteAuthor(author.id, author.name)}
                                  disabled={deleteAuthor.isPending}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  {deleteAuthor.isPending ? 'Deleting...' : 'Delete'}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No authors found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AdminAuthors;
